import { v4 as uuidv4 } from "uuid"

import { Status } from "@/models/Status"

import { Base } from "./Base"
import { StructuredDescription } from "./EditorData"
import { FileMetaData } from "./File"
import { FeContext, ItemSeverityLevel, ItemStatus, ItemType } from "./Item"
import { ReferencedResourcePreviews } from "./Search"
import { Team } from "./Team"
import { Type, typeCreateById } from "./Type"

import type { MilestoneSimplify } from "./Milestone"

export type EpicStatus =
  | "ALL"
  | "NOT_STARTED"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "ARCHIVED"
  | "OBSOLETE"

const epicStatuses = [
  {
    id: "ALL",
    label: "All Epics"
  },
  {
    id: "NOT_STARTED",
    label: "Not Started"
  },
  {
    id: "IN_PROGRESS",
    label: "In Progress"
  },
  {
    id: "COMPLETED",
    label: "Completed"
  },
  {
    id: "ARCHIVED",
    label: "Archived"
  },
  {
    id: "OBSOLETE",
    label: "Obsolete"
  }
] as Status[]

export interface AddEpicStory {
  inEpic: boolean
  epicIds: string[]
}
export interface tShort {
  id: string
  shortLabel?: string
  label?: string
}

export interface CreateEpicInMilestone {
  previousItemId?: string
  nextItemId?: string
  lowest?: boolean
  milestoneId: string
}

export interface CreateEpic {
  name: string
  size?: string
  label?: string
  projectId?: string
  createEpicPriorityInMilestoneDto?: CreateEpicInMilestone
  teamId?: string
}

export class EpicItem {
  id = ""
  sequenceNumber = 1
  title = ""
  itemType: ItemType = "STORY"
  itemTypeParsed: Type = typeCreateById({ id: "STORY" })
  status: ItemStatus = "NOT_STARTED"
  statusParsed: Status = Status.createById({ id: "NOT_STARTED" })
  closed = false
  createdDate = ""
  epicPriority = 0
  estimation = 0
  label = ""
  projectId = ""
  severityLevel: ItemSeverityLevel = "NONE"
  constructor(props?: Partial<EpicItem>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.itemTypeParsed = typeCreateById({ id: this.itemType })
    this.statusParsed = Status.createById({ id: this.status })
  }
}

export class Epic extends Base<Epic> {
  id: string
  name: string
  label?: string | null
  roadMapPriority: number
  milestonePriority: number
  status: EpicStatus
  parsedStatus: Status
  sequenceNumber: number
  teams: Team[]
  totalStoryPoints: number
  itemsCount: number
  percentComplete: number
  size?: string | null
  createEpicPriorityInMilestoneDto?: CreateEpicInMilestone | null
  teamId?: string
  doneItemsCount: number
  itemsInMilestoneCount: number
  doneItemsInMilestoneCount: number
  inProgressItemsInMilestoneCount: number
  notStartedItemsInMilestoneCount: number
  percentCompleteInMilestone: number
  projectId?: string
  items?: EpicItem[]
  fileMetadata?: FileMetaData[]
  structuredDescription?: StructuredDescription
  referencedResourcePreviews?: ReferencedResourcePreviews
  feContext: FeContext
  convertedFromItemId?: string
  milestone?: MilestoneSimplify | null

  constructor(props?: Epic) {
    super(props)
    this.setDefaultData()
    if (!props) return
    this.items = props.items ? props.items.map(item => new EpicItem(item)) : []
    this.id = props.id ?? ""
    this.name = props.name ?? ""
    this.roadMapPriority = props.roadMapPriority ?? 0
    this.milestonePriority = props.milestonePriority ?? 0
    this.status = props.status ?? "NOT_STARTED"
    this.parsedStatus = this.getParsedStatus(props.status ?? "NOT_STARTED")
    this.sequenceNumber = props.sequenceNumber ?? 1
    this.teams = props.teams ?? []
    this.totalStoryPoints = props.totalStoryPoints ?? 0
    this.itemsCount = props.itemsCount ?? 0
    this.percentComplete = props.percentComplete ?? 0
    this.size = props.size ?? null
    this.label = props.label ?? null
    this.createEpicPriorityInMilestoneDto = props.createEpicPriorityInMilestoneDto ?? null
    this.teamId = props.teamId ?? ""
    this.doneItemsCount = props.doneItemsCount ?? 0
    this.itemsInMilestoneCount = props.itemsInMilestoneCount ?? 0
    this.doneItemsInMilestoneCount = props.doneItemsInMilestoneCount ?? 0
    this.inProgressItemsInMilestoneCount = props.inProgressItemsInMilestoneCount ?? 0
    this.notStartedItemsInMilestoneCount = props.notStartedItemsInMilestoneCount ?? 0
    this.percentCompleteInMilestone = props.percentCompleteInMilestone ?? 0
    this.projectId = props.projectId ?? ""
    this.fileMetadata = props.fileMetadata ? props.fileMetadata.map(fm => new FileMetaData(fm)) : []
    this.structuredDescription = props.structuredDescription
      ? new StructuredDescription(props.structuredDescription)
      : undefined
    this.referencedResourcePreviews = props.referencedResourcePreviews
    this.feContext = { changeId: uuidv4() }
    this.convertedFromItemId = props.convertedFromItemId
    this.milestone = props.milestone ?? null
  }

  setDefaultData() {
    this.id = ""
    this.name = ""
    this.roadMapPriority = 0
    this.milestonePriority = 0
    this.status = "NOT_STARTED"
    this.parsedStatus = this.getParsedStatus("NOT_STARTED")
    this.sequenceNumber = 1
    this.totalStoryPoints = 0
    this.itemsCount = 0
    this.percentComplete = 0
    this.label = null
    this.doneItemsCount = 0
    this.itemsInMilestoneCount = 0
    this.doneItemsInMilestoneCount = 0
    this.inProgressItemsInMilestoneCount = 0
    this.notStartedItemsInMilestoneCount = 0
    this.percentCompleteInMilestone = 0
    this.teamId = ""
    this.projectId = ""
    this.items = []
  }

  getJsonObj() {
    return {
      name: this.name,
      size: this.size,
      label: this.label,
      createEpicPriorityInMilestoneDto: this.createEpicPriorityInMilestoneDto,
      teamId: this.teamId
    }
  }

  static create(props: Epic): Epic {
    return new Epic(props)
  }

  static createDefault(): Epic {
    return new Epic()
  }

  static get statuses(): Status[] {
    return epicStatuses
  }

  get isCompleted() {
    return this.parsedStatus.id === "COMPLETED"
  }

  getParsedStatus(id: string) {
    const epic = Epic.statuses.find((status: Status) => status.id === id)
    return epic ? epic : ({ id: "", label: "" } as Status)
  }
}
